import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TextField, Autocomplete, Button, Grid, Box, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import DynamicTable from '../../../components/Dynamic-table';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmationModal from '../../../components/Modals/delete-modal';

const Antibiotic = () => {
    const [drugName, setDrugName] = useState('');
    const [allDosageNotations, setDosage] = useState({
        med_notation: "",
        eng_notation: "",
        urdu_notation: ""
    });
    const [route] = useState('IV');
    const [frequency] = useState('stat');
    const [dosageOptions, setDosageOptions] = useState<any[]>([]);
    const [antibiotics, setAntibiotics] = useState<any[]>([]);
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState<any>("");
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState("");
    const [editData, setEditData] = useState(null);

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
        newPage: number
    ) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    const getAllAntibiotics = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_IPD_API_URL}/antibiotics-prophylaxis/getAllBySearch`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                }
            })
            setAntibiotics(response?.data?.data)
        } catch (err) {
            console.error("Failed to fetch dosages", err);
        }
    }
    useEffect(() => {
        getAllAntibiotics();
    }, []);

    useEffect(() => {
        const getAllDosage = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_OPD_API_URL}/dosage/getAllBySearch`, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    }
                })
                setDosageOptions(response?.data?.data)
            } catch (err) {
                console.error("Failed to fetch dosages", err);
            }
        }
        getAllDosage();
    }, []);

    const handleChangeDosage = async (value: any) => {
        setDosage({
            med_notation: value?.med_notation || '',
            eng_notation: value?.eng_notation || '',
            urdu_notation: value?.urdu_notation || ''
        })
    }

    const handleEditClick = (id: any, data: any) => {
        setEditData(id);
        setDrugName(data?.drugName);
        setDosage(data?.allDosageNotations);
    }
    const handleDeleteModalClose = () => {
        setIsDeleteModalOpen(false);
    };

    const handleDeleteClick = (id: any) => {
        setIsDeleteModalOpen(true);
        setSelectedItemId(id);
    };

    const handleDeleteConfirm = async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Authorization token is missing");
                return;
            }
            const response = await axios.delete(
                `${process.env.REACT_APP_IPD_API_URL}/antibiotics-prophylaxis/deleteById/${selectedItemId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            getAllAntibiotics();
            toast.success(response?.data?.message);
            setDrugName('');
            setDosage({
                med_notation: "",
                eng_notation: "",
                urdu_notation: "",
            });
            setEditData(null);
        } catch (error: any) {
            toast.error(error.message);
        }
        setIsDeleteModalOpen(false);
    };
    const handleSubmit = async () => {
        if (!drugName || !allDosageNotations?.med_notation) {
            toast.error("Please fill all required fields");
            return;
        }
        const formData = {
            drugName,
            allDosageNotations,
            route,
            frequency,
        };
        if (editData) {
            try {
                const response = await axios.patch(
                    `${process.env.REACT_APP_IPD_API_URL}/antibiotics-prophylaxis/updateById/${editData}`,
                    formData,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${localStorage.getItem("token")}`,
                        },
                    }
                );
                if (!response?.data?.isSuccess) {
                    toast.error(response.data.message);
                } else {
                    toast.success(response.data.message);
                    setDrugName('');
                    setDosage({
                        med_notation: "",
                        eng_notation: "",
                        urdu_notation: "",
                    });
                    getAllAntibiotics();
                    setEditData(null);
                }
            } catch (error: any) {
                toast.error(error.message || "Something went wrong");
            }
        }
        else {
            try {
                const response = await axios.post(
                    `${process.env.REACT_APP_IPD_API_URL}/antibiotics-prophylaxis/addDrug`,
                    formData,
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem("token")}`,
                        },
                    }
                );
                if (response?.data?.isSuccess) {
                    setDrugName('');
                    setDosage({
                        med_notation: "",
                        eng_notation: "",
                        urdu_notation: "",
                    });
                    toast.success(response?.data?.message);
                    getAllAntibiotics()
                }
            } catch (error) {
                console.error('Error submitting the form:', error);
            }
        }
    };

    return (
        <Box sx={{ background: '#fff', p: 1 }}>
            <Typography variant='h5' sx={{ fontWeight: 'bold' }}>Add Prophylactic Antibiotic</Typography>
            <Grid container spacing={1}>
                <Grid item md={4} sm={4} xs={12}>
                    <form onSubmit={(e) => e.preventDefault()} style={{
                        padding: '10px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px'
                    }}>
                        <TextField
                            label="Drug Name"
                            variant="outlined"
                            fullWidth
                            value={drugName}
                            onChange={(e) => setDrugName(e.target.value)}
                            size='small'
                            className='search-field'
                        />

                        <Autocomplete
                            options={dosageOptions || []}
                            getOptionLabel={(option) => option?.med_notation || ''}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Dosage"
                                    variant="outlined"
                                    fullWidth
                                    size='small'
                                    className='search-field'
                                />
                            )}
                            value={allDosageNotations || ''}
                            onChange={(e, newValue) => handleChangeDosage(newValue)}
                        />

                        <TextField
                            label="Route"
                            variant="outlined"
                            fullWidth
                            value={route}
                            InputProps={{
                                readOnly: true,
                            }}
                            size='small'
                            className='search-field'
                        />

                        <TextField
                            label="Frequency"
                            variant="outlined"
                            fullWidth
                            size='small'
                            className='search-field'
                            value={frequency}
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                        >
                            Submit
                        </Button>


                    </form>
                </Grid>
                <Grid item md={8} sm={8} xs={12}>
                    <DynamicTable
                        headers={["SN", "Drug Name", "Dosage", "Route", "Frequency", "Actions"]}
                        data={antibiotics?.map((item: any, index: any) => [
                            page * rowsPerPage + index + 1,
                            item?.drugName,
                            item?.allDosageNotations?.med_notation,
                            item?.route,
                            item?.frequency,
                        ])}
                        loading={false}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                        totalRows={total}
                        actionButtons={[
                            {
                                icon: (
                                    <EditIcon sx={{ color: "#FFB800", cursor: "pointer" }} />
                                ),
                                onClick: (rowIndex: any) =>
                                    handleEditClick(
                                        antibiotics[rowIndex]?._id,
                                        antibiotics[rowIndex]
                                    ),
                            },
                            {
                                icon: (
                                    <DeleteIcon sx={{ color: "#FF5454", cursor: "pointer" }} />
                                ),
                                onClick: (rowIndex: any) =>
                                    handleDeleteClick(antibiotics[rowIndex]?._id),
                            },
                        ]}
                    />
                </Grid>
            </Grid>
            <DeleteConfirmationModal
                open={isDeleteModalOpen}
                handleClose={handleDeleteModalClose}
                handleConfirm={handleDeleteConfirm}
            />
        </Box>
    );
};

export default Antibiotic;
